
import { OpenAI } from "langchain/llms/openai";
import { PromptTemplate } from "langchain/prompts";
import { LLMChain } from "langchain/chains";
import { AIRequestOptions } from "./openAIClient";
import { logger } from "@/lib/logger";

// Define router types for different use cases
enum RouterType {
  MARKETING = "marketing",
  SUPPORT = "support",
  DATA_ENTRY = "data_entry",
  SALES = "sales",
}

interface RouterMapping {
  type: RouterType;
  model: string;
  temperature: number;
  maxTokens: number;
}

// Router mapping configuration
const ROUTER_CONFIGS: RouterMapping[] = [
  {
    type: RouterType.MARKETING,
    model: "gpt-4o",
    temperature: 0.7,
    maxTokens: 1500,
  },
  {
    type: RouterType.SUPPORT,
    model: "gpt-4o-mini",
    temperature: 0.3,
    maxTokens: 1000,
  },
  {
    type: RouterType.DATA_ENTRY,
    model: "gpt-4o-mini",
    temperature: 0.1,
    maxTokens: 500,
  },
  {
    type: RouterType.SALES,
    model: "gpt-4o",
    temperature: 0.6,
    maxTokens: 1000,
  },
];

export class LangChainRouter {
  private apiKey: string = "";
  private _isInitialized = false;
  
  /**
   * Initialize the LangChain router with an API key
   */
  public initialize(apiKey: string): void {
    if (!apiKey) {
      throw new Error("OpenAI API key is required for LangChain router");
    }
    
    this.apiKey = apiKey;
    this._isInitialized = true;
    
    // Configure LangSmith if environment variables are set
    const langsmithApiKey = import.meta.env.VITE_LANGSMITH_API_KEY;
    const langsmithProject = import.meta.env.VITE_LANGSMITH_PROJECT;
    const langsmithEndpoint = import.meta.env.VITE_LANGSMITH_ENDPOINT;
    const langsmithTracing = import.meta.env.VITE_LANGSMITH_TRACING;
    
    if (langsmithApiKey) {
      process.env.LANGCHAIN_API_KEY = langsmithApiKey;
    }
    
    if (langsmithProject) {
      process.env.LANGCHAIN_PROJECT = langsmithProject;
    }
    
    if (langsmithEndpoint) {
      process.env.LANGCHAIN_ENDPOINT = langsmithEndpoint;
    }
    
    if (langsmithTracing) {
      process.env.LANGCHAIN_TRACING_V2 = langsmithTracing;
    }
    
    logger.info({
      message: "LangChain router initialized",
      context: { 
        langsmith: !!langsmithApiKey ? "configured" : "not configured"
      }
    });
  }
  
  /**
   * Check if the router is initialized
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Determine the best model and config based on the request content
   */
  private async determineRouterType(prompt: string): Promise<RouterMapping> {
    try {
      // Use a lightweight model to classify the request type
      const classifier = new OpenAI({
        openAIApiKey: this.apiKey,
        modelName: "gpt-4o-mini",
        temperature: 0,
        maxTokens: 50,
      });

      // Create a template for classification
      const classifierTemplate = PromptTemplate.fromTemplate(
        "Classify the following request into one of these categories: marketing, support, data_entry, sales.\n\nRequest: {prompt}\n\nCategory:"
      );

      // Create a chain for classification
      const classifierChain = new LLMChain({
        llm: classifier,
        prompt: classifierTemplate,
      });

      // Run the classifier
      const { text: routerType } = await classifierChain.call({ prompt });
      const cleanType = routerType.trim().toLowerCase() as RouterType;

      // Find the appropriate configuration
      const config = ROUTER_CONFIGS.find(
        (config) => config.type === cleanType
      ) || ROUTER_CONFIGS[0]; // Default to marketing if no match

      logger.info({
        message: "LangChain router selected model",
        context: { 
          routerType: cleanType, 
          model: config.model,
          temperature: config.temperature
        },
      });

      return config;
    } catch (error) {
      logger.error({
        message: "Error determining router type",
        error: error instanceof Error ? error : new Error(String(error)),
      });
      
      // Default to marketing configuration on error
      return ROUTER_CONFIGS[0];
    }
  }

  /**
   * Generate a response using the LangChain router
   */
  public async generateResponse(options: AIRequestOptions): Promise<string> {
    if (!this.isInitialized) {
      throw new Error("LangChain router not initialized. Call initialize() first.");
    }

    try {
      const { systemPrompt, userPrompt, attachment } = options;
      
      // Determine the best model configuration for this request
      const routerConfig = await this.determineRouterType(userPrompt);
      
      // Initialize the model with the selected configuration
      const model = new OpenAI({
        openAIApiKey: this.apiKey,
        modelName: routerConfig.model,
        temperature: routerConfig.temperature,
        maxTokens: routerConfig.maxTokens,
      });

      // Prepare the full prompt with system instructions and user input
      let fullPrompt = systemPrompt + "\n\n" + userPrompt;
      if (attachment) {
        fullPrompt += `\n\nThe user has attached a file named: ${attachment}`;
      }

      // Create a template for the response
      const promptTemplate = PromptTemplate.fromTemplate("{fullPrompt}");
      
      // Create and execute the chain
      const chain = new LLMChain({ 
        llm: model, 
        prompt: promptTemplate 
      });
      
      const { text: response } = await chain.call({ fullPrompt });
      return response;
    } catch (error) {
      logger.error({
        message: "Error generating response with LangChain",
        error: error instanceof Error ? error : new Error(String(error)),
      });
      throw error;
    }
  }
}
